import { useTranslation } from 'react-i18next'
import { format, isValid } from 'date-fns'

import { Badge, BadgeVariant, CardContent, CardDashboard } from '@percent/lemonade'
import { KycAnalysisProps } from './KycAnalysis.types'
import { KycRiskLevel, KycVerificationStatus } from '@percent/admin-dashboard/api/types'

const statusVariantMap: Record<KycVerificationStatus, BadgeVariant> = {
  pending: 'informative',
  awaiting_input: 'informative',
  review: 'informative',
  approved: 'positive',
  declined: 'critical',
  abandoned: 'warning',
  timed_out: 'warning',
  error: 'warning'
}

const riskVariantMap: Record<KycRiskLevel, BadgeVariant> = {
  low: 'positive',
  moderate: 'informative',
  high: 'warning',
  very_high: 'critical'
}

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return null

  const date = new Date(value)

  return isValid(date) ? format(date, 'dd MMMM yyyy, HH:mm') : null
}

const isSafeHttpUrl = (value: string) => /^https?:\/\//i.test(value)

export function KycAnalysis({ kyc }: Readonly<KycAnalysisProps>) {
  const { t } = useTranslation()

  if (!kyc || !('status' in kyc)) return null

  const notAvailable = t('status.na')

  const createdAt = formatDateTime(kyc.createdAt)
  const updatedAt = formatDateTime(kyc.updatedAt)
  const completedAt = formatDateTime(kyc.completedAt)
  const expiresAt = formatDateTime(kyc.expiresAt)

  return (
    <CardDashboard title={t('typography.kycAnalysis')} data-testid="kyc-analysis">
      <CardContent title={t('typography.kycStatus')}>
        <Badge variant={statusVariantMap[kyc.status] ?? 'default'}>
          {t(`typography.kyc.status.${kyc.status}`, { defaultValue: kyc.status })}
        </Badge>
      </CardContent>
      <CardContent title={t('typography.kycRiskLevel')}>
        {kyc.riskLevel ? (
          <Badge variant={riskVariantMap[kyc.riskLevel] ?? 'default'}>
            {t(`typography.kyc.riskLevel.${kyc.riskLevel}`, { defaultValue: kyc.riskLevel })}
          </Badge>
        ) : (
          <span>{notAvailable}</span>
        )}
      </CardContent>
      <CardContent title={t('typography.kycFraudScore')}>
        {/* ?? not ||: a fraudScore of 0 is a valid value and must render as "0", not N/A. */}
        <span>{kyc.fraudScore ?? notAvailable}</span>
      </CardContent>
      <CardContent title={t('typography.kycProvider')}>
        <span>{kyc.provider ?? notAvailable}</span>
      </CardContent>
      <CardContent title={t('typography.kycWorkflowReference')}>
        <span>{kyc.workflowReference ?? notAvailable}</span>
      </CardContent>
      <CardContent title={t('typography.kycWorkflowRunId')}>
        <span>{kyc.providerWorkflowRunId ?? notAvailable}</span>
      </CardContent>
      <CardContent title={t('typography.kycDashboardLink')}>
        {kyc.dashboardLinkUrl && isSafeHttpUrl(kyc.dashboardLinkUrl) ? (
          <a href={kyc.dashboardLinkUrl} target="_blank" rel="noreferrer">
            {kyc.dashboardLinkUrl}
          </a>
        ) : (
          <span>{kyc.dashboardLinkUrl ?? notAvailable}</span>
        )}
      </CardContent>
      <CardContent title={t('typography.kycCreatedAt')}>
        <span>{createdAt || notAvailable}</span>
      </CardContent>
      <CardContent title={t('typography.kycUpdatedAt')}>
        <span>{updatedAt || notAvailable}</span>
      </CardContent>
      <CardContent title={t('typography.kycCompletedAt')}>
        <span>{completedAt || notAvailable}</span>
      </CardContent>
      <CardContent title={t('typography.kycExpiresAt')}>
        <span>{expiresAt || notAvailable}</span>
      </CardContent>
    </CardDashboard>
  )
}
